import { useFormikContext } from 'formik';
import { Dropdown, ErrorContainer, LabelComponent, Stack, Tooltip } from 'components';
import { useAppSelector } from 'store/hooks';
import { getLsUserRoleObject } from 'modules/networkTools/localStorageTokens';
import clsx from 'clsx';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  HowDidYouReachThemResetFields,
  WhoWereYouTryingToReachResetFields
} from '../whoDidYouReach/whoDidYouReach';
import { WhichProviderResetFields } from '../whoDidYouReach/whoDidYouReach';
import { useNoteFormContext } from '../../context';
import style from './noteType.module.scss';

interface NoteTypeProps {
  disabled: boolean;
}

const MANAGER_REVIEW_NOTE_PRESET =
  "CHW reviewed client's applicable history, changes since last staffing, goals, and interventions to be used to help the client achieve next steps with supervisor.";

export { getNoteTypeData, mapNoteTypesToOptions } from './noteTypeData';
export type { NoteTypeOption } from './noteTypeData';

export const NoteType = ({ disabled }: NoteTypeProps) => {
  const { enableMpiUpdate } = useFlags();
  const {
    meta: { noteTypes }
  } = useNoteFormContext();
  const formik = useFormikContext<{
    noteType: string | null;
    note: string | null;
  }>();

  const chwId = useAppSelector((state) => state.clients.client?.data?.chwId);

  const managers = useAppSelector((state) => state.clients.managers.data);
  const userRole = getLsUserRoleObject();
  const userId = userRole.userId;
  const currentManager = managers.find((manager) => manager.id.toString() === userId.toString());

  return (
    <>
      <Stack display="flex" flexDirection="row" alignItems="center" gap="4px">
        <LabelComponent mandatory text="Note Type" />
        {enableMpiUpdate && (
          <Tooltip
            placement="right"
            toolTipText={
              <Stack className={style.noteTypeInfo}>
                <p>
                  Don't see the Note Type you're looking for? Make sure your client is Enrolled!
                </p>
              </Stack>
            }
          >
            <span className={clsx('material-icons', style.infoIcon)}>info</span>
          </Tooltip>
        )}
      </Stack>
      <Dropdown
        smallDropdown
        withGradient
        label="Select"
        data={noteTypes}
        onChange={(e) => {
          formik.handleChange(e);

          if (e.target.value === 'MANAGER_REVIEW') {
            [
              { key: 'startTimeOfEncounter', value: null },
              { key: 'nextContact', value: null },
              { key: 'whoDidYouReach', value: [] },
              { key: 'typeOfSupport', value: [] },
              { key: 'selectedZCodes', value: [] },
              ...WhoWereYouTryingToReachResetFields,
              ...HowDidYouReachThemResetFields,
              ...WhichProviderResetFields
            ].forEach((field) => {
              formik.setFieldValue(field.key, field.value);
              formik.setFieldTouched(field.key, false);
            });
            formik.setFieldValue('timeSpentMin', '10');
            formik.setFieldValue('chwId', chwId?.toString() || null);
            formik.setFieldValue('managerId', currentManager?.id.toString() || null);
            formik.setFieldValue(
              'note',
              formik.values.note && formik.values.note?.length > 0
                ? `${formik.values.note} ${MANAGER_REVIEW_NOTE_PRESET}`
                : MANAGER_REVIEW_NOTE_PRESET
            );
            formik.setFieldValue('documentationTime', '1');
          }
          if (formik.values.noteType === 'MANAGER_REVIEW' && e.target.value !== 'MANAGER_REVIEW') {
            formik.setFieldValue('timeSpentMin', '');
            if (formik.values.note === MANAGER_REVIEW_NOTE_PRESET) {
              formik.setFieldValue('note', '');
            }
          }
        }}
        onBlur={formik.handleBlur}
        name="noteType"
        id="noteType"
        value={formik.values.noteType ?? ''}
        disabled={disabled}
      />
      <ErrorContainer
        visible={formik.touched.noteType && formik.errors.noteType}
        errors={formik.errors}
        name="noteType"
      />
    </>
  );
};
